import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Container,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import Pagination from 'components/Pagination';

import {
  LOCATIONS,
  TCXH_FEEDBACK_STATUS,
  themeColor,
} from 'constants/index';
import cn from 'utils/classnames';
import getTotalPage from 'utils/totalPage';
import { getParamsFormObject } from 'utils';
import { actionGetSocialOrganizationFeedbacks } from 'store/manager-tcxh-feedback/action';

import DataTableTcxhFeedbackList from './DataTableTcxhFeedbackList';
import styles from './ManagerReflect.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý khiếu nại',
    isActive: false,
    link: LOCATIONS.MANAGER_COMPLAINT,
  },
  {
    name: 'Tiếp nhận phản ánh của TCXH',
    isActive: true,
    link: LOCATIONS.MANAGER_REFLECT || LOCATIONS.MANAGER_TCXH_FEEDBACK,
  },
];

const TAB = {
  ALL: '',
  PENDING: TCXH_FEEDBACK_STATUS.PENDING,
  APPROVED: TCXH_FEEDBACK_STATUS.APPROVED,
  REJECTED: TCXH_FEEDBACK_STATUS.REJECTED,
};

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: TAB.ALL,
  },
  {
    title: 'Chờ xử lý',
    value: TAB.PENDING,
  },
  {
    title: 'Đã xử lý',
    value: TAB.APPROVED,
  },
  {
    title: 'Từ chối',
    value: TAB.REJECTED,
  },
];

const DEFAULT_LIMIT = 20;

function ManagerReflect() {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    socialOrganizationFeedbacks,
    totalSocialOrganizationFeedbacks,
    isLoadingGetSocialOrganizationFeedbacks,
  } = useSelector((state) => state.ManagerTcxhFeedback || {});

  const urlParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const urlPage = useMemo(
    () => Number(urlParams.get('page') || 1),
    [urlParams],
  );

  const searchQueries = useMemo(() => ({
    page: urlPage,
    limit: Number(urlParams.get('limit') || DEFAULT_LIMIT),
    q: urlParams.get('q') || '',
    status: urlParams.get('status') || '',
  }), [urlPage, urlParams]);

  const activeTab = searchQueries.status || TAB.ALL;

  useEffect(() => {
    dispatch(
      actionGetSocialOrganizationFeedbacks({
        page: searchQueries.page,
        limit: searchQueries.limit,
        status: searchQueries.status || undefined,
        q: searchQueries.q?.trim() || undefined,
      }),
    );
  }, [dispatch, searchQueries]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: searchQueries.q,
    },
    onSubmit: (values) => {
      history.push({
        search: getParamsFormObject({
          q: values.q?.trim() || '',
          status: searchQueries.status || '',
          page: 1,
          limit: DEFAULT_LIMIT,
        }),
      });
    },
  });

  const onResetForm = useCallback(() => {
    validation.resetForm({
      values: {
        q: '',
      },
    });

    history.push({
      search: getParamsFormObject({
        status: searchQueries.status || '',
        page: 1,
        limit: DEFAULT_LIMIT,
      }),
    });
  }, [history, searchQueries.status, validation]);

  const onChangeTab = useCallback(
    (tabValue) => () => {
      history.push({
        search: getParamsFormObject({
          ...searchQueries,
          status: tabValue,
          page: 1,
        }),
      });
    },
    [history, searchQueries],
  );

  const onChangePage = useCallback(
    (selectedItem) => {
      history.push({
        search: getParamsFormObject({
          ...searchQueries,
          page: selectedItem.selected + 1,
        }),
      });
    },
    [history, searchQueries],
  );

  const onOpenDetail = useCallback(
    (feedback) => {
      history.push(`${LOCATIONS.MANAGER_TCXH_FEEDBACK}/${feedback._id || feedback.id}`);
    },
    [history],
  );

  const pageCount = getTotalPage(
    totalSocialOrganizationFeedbacks, searchQueries.limit || DEFAULT_LIMIT);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name="Tiếp nhận phản ánh của TCXH" />

      <Container fluid>
        {/* Render Breadcrumb */}
        <Breadcrumb
          pageTitle="Tiếp nhận phản ánh của TCXH"
          breadcrumbList={breadcrumbList}
        />

        {/* Search & Filter Card */}
        <Card className="Section">
          <form onSubmit={validation.handleSubmit}>
            <Row className="Section__Search__Filter">
              <div className="mb-24 flex-1">
                <Label className="Section__Search__Label">Tìm kiếm</Label>

                <div className="Section__Search__Container">
                  <AppInputValidation
                    name="q"
                    id="q"
                    type="text"
                    placeholder="Tìm theo tên tổ chức, email hoặc số quyết định thành lập"
                    validation={validation}
                    inputClassName={cn('Section__Search__Input')}
                  />

                  <i className="fas fa-search Section__Search__Icon__Search" />
                </div>
              </div>
            </Row>

            <div className="d-flex mt-24">
              <Button
                outline
                classes="Section__Search__Button__ReType"
                color={themeColor.LIGHT}
                onClick={onResetForm}
              >
                Nhập lại
              </Button>

              <Button
                classes="Section__Search__Button__Search"
                type="submit"
              >
                Tìm kiếm
              </Button>
            </div>
          </form>
        </Card>

        {/* Data Table Card */}
        <Card className="Section">
          <div className="d-flex Table__Header mb-3">
            <Nav pills className="col-md-8">
              {NAV_TAB.map((tab) => (
                <NavItem key={tab.title} className="col-md-3">
                  <NavLink
                    className={activeTab === tab.value ? 'active' : ''}
                    onClick={onChangeTab(tab.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{tab.title}</span>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>

          <Label className="Table__Header__Total">
            {`${totalSocialOrganizationFeedbacks} phản ánh`}
          </Label>

          <TabContent activeTab={activeTab || 'ALL'} className="Table__Body">
            <TabPane tabId={activeTab || 'ALL'}>
              <DataTableTcxhFeedbackList
                isLoading={isLoadingGetSocialOrganizationFeedbacks}
                feedbacks={socialOrganizationFeedbacks}
                onOpenDetail={onOpenDetail}
                page={urlPage}
                limit={searchQueries.limit || DEFAULT_LIMIT}
              />
            </TabPane>
          </TabContent>
        </Card>

        {/* Pagination */}
        {totalSocialOrganizationFeedbacks > 0 && (
          <Pagination
            page={urlPage}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>
    </div>
  );
}

export default ManagerReflect;
